/**
 * terraceRoofSymbolRenderer.js — floor-plan roof symbol for terraces (#693, Chunk 1C/2B)
 *
 * A terrace is a flat Area (Rect / Polygon / Bezier-Path). Its roofing
 * (`data.ueberdachung`) is invisible in the plan view without a mark. This
 * module draws the classic floor-plan convention for something that sits ABOVE
 * the cutting plane — a dashed outline — plus:
 *   - Terrassendach: small solid squares at the corners for the supports. The
 *     squares sit at the object's local bounding-box corners, matching the OBB
 *     corners the 3D build uses for its Stützen (buildTerraceRoof).
 *   - Markise (Chunk 2B): a solid rail along the anchor edge (the wall side) plus
 *     a dashed strip cantilevering inward over the terrace by the Ausfall — the
 *     same anchor edge and the same covered area as buildTerraceAwning in 3D.
 *   - Wintergarten (Chunk 3B): a SOLID (non-dashed) double outline with a filled
 *     interior. Unlike the roof/markise (which sit above the cutting plane →
 *     dashed), a Wintergarten is room-forming — its glazed walls cut through the
 *     plane, so the floor-plan convention is a solid, closed outline. The double
 *     line reads as wall thickness; the fill sets the room off from the bare slab.
 *
 * Unlike the gate symbol (which REPLACES a Rect's render), this DECORATES the
 * terrace's own render: the slab still draws through its shape-specific
 * renderer, and the roof overlay is painted on top. That keeps it correct for
 * all three terrace shapes (Rect / Polygon / Path) without knowing the vertex
 * structure — every shape renders centered at the local origin with extents
 * ±width/2 × ±height/2.
 *
 * The geometry lives in pure functions (computeTerraceRoofSymbol /
 * computeMarkiseSymbol / computeWintergartenSymbol) so the layout is
 * unit-testable without a canvas. The renderer reads `data.ueberdachung` (and
 * the markise fields) live, so switching back to 'keine' — or retracting the
 * awning — updates the overlay on the next render without any un-install step.
 */
import { GARDEN_OBJECT_TYPES_CONFIG } from '../../../config/gardenObjectTypes';

export const TERRACE_ROOF_LINE_WIDTH = 1.5;
export const TERRACE_SUPPORT_LINE_WIDTH = 1;
export const MARKISE_RAIL_LINE_WIDTH = 3; // the mounting rail on the wall side, drawn solid
export const TERRACE_ROOF_SELECTED_COLOR = '#10B981';
export const TERRACE_ROOF_DEFAULT_COLOR = '#6B7280';
export const TERRACE_ROOF_INSET = 4; // px inset of the dashed outline from the footprint edge
export const TERRACE_SUPPORT_SIZE = 6; // px side of the corner support squares
export const TERRACE_ROOF_DASH = [6, 4];
export const WINTERGARTEN_WALL_INSET = 4; // px of the inner (wall-thickness) outline

/** Roofing variants that render a plan symbol. */
export const ROOFED_VARIANTS = new Set(['terrassendach', 'markise', 'wintergarten']);

/** Config default of a terrace mvp property (single source of truth for fallbacks). */
function terraceConfigDefault(key) {
  const entry = Object.values(GARDEN_OBJECT_TYPES_CONFIG).find((t) => t.id === 'terrasse');
  return entry?.properties?.mvp?.[key]?.defaultValue ?? null;
}

/** Resolve markiseAusfall (cm) from object data, falling back to the config default. */
export function resolveMarkiseAusfallCm(data) {
  const v = data?.markiseAusfall;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  return terraceConfigDefault('markiseAusfall') ?? 0;
}

/** Resolve a fabric object's garden type id (both key conventions in use). */
export const getGardenObjectType = (obj) =>
  obj?.data?.objectType || obj?.data?.gardenObjectType;

/** Whether an object is a terrace. */
export const isTerrace = (obj) => getGardenObjectType(obj) === 'terrasse';

/** Whether a terrace currently carries a roofing that renders a plan symbol. */
export const usesTerraceRoofSymbol = (obj) =>
  isTerrace(obj) && ROOFED_VARIANTS.has(obj?.data?.ueberdachung);

/**
 * Compute the roof-symbol geometry in the terrace's local coordinate system.
 * Local origin = object center; the footprint spans ±width/2 × ±height/2.
 *
 * @param {object} params
 * @param {number} params.width  - footprint width in local px
 * @param {number} params.height - footprint height in local px
 * @param {string} params.ueberdachung - roofing variant
 * @returns {null | { outline: {x,y,width,height}, supports: {x,y,size}[] }}
 *   Returns null when the variant renders no symbol ('keine' / unknown).
 */
export function computeTerraceRoofSymbol({ width, height, ueberdachung }) {
  if (ueberdachung !== 'terrassendach') return null;

  const w = Math.max(0, width || 0);
  const h = Math.max(0, height || 0);
  const halfW = w / 2;
  const halfH = h / 2;

  // Dashed outline, inset from the footprint edge so it reads as "above" the slab.
  const inset = Math.min(TERRACE_ROOF_INSET, halfW, halfH);
  const outline = {
    x: -halfW + inset,
    y: -halfH + inset,
    width: Math.max(0, w - 2 * inset),
    height: Math.max(0, h - 2 * inset),
  };

  // Corner supports: small squares at the four local-bbox corners (= OBB corners
  // in world space), pulled inward by half their size so they sit fully inside.
  const half = TERRACE_SUPPORT_SIZE / 2;
  const sx = Math.max(0, halfW - half);
  const sy = Math.max(0, halfH - half);
  const supports = [
    { cx: -sx, cy: -sy },
    { cx: sx, cy: -sy },
    { cx: sx, cy: sy },
    { cx: -sx, cy: sy },
  ].map(({ cx, cy }) => ({ x: cx - half, y: cy - half, size: TERRACE_SUPPORT_SIZE }));

  return { outline, supports };
}

/**
 * Draw a computed roof symbol onto a context already transformed into the
 * terrace's local space (fabric calls _render with such a context).
 */
export function drawTerraceRoofSymbol(ctx, symbol, { lineColor, fillColor }) {
  if (!symbol) return;
  const { outline, supports } = symbol;

  ctx.save();

  // Dashed roof outline (above the cutting plane → dashed convention).
  ctx.setLineDash?.(TERRACE_ROOF_DASH);
  ctx.lineWidth = TERRACE_ROOF_LINE_WIDTH;
  ctx.strokeStyle = lineColor;
  ctx.strokeRect(outline.x, outline.y, outline.width, outline.height);

  // Corner supports: solid squares, no dash.
  ctx.setLineDash?.([]);
  ctx.lineWidth = TERRACE_SUPPORT_LINE_WIDTH;
  ctx.fillStyle = fillColor;
  supports.forEach((s) => {
    ctx.fillRect(s.x, s.y, s.size, s.size);
    ctx.strokeRect(s.x, s.y, s.size, s.size);
  });

  ctx.restore();
}

/**
 * Compute the markise plan symbol in the terrace's local coordinate system.
 * Local origin = object center; the footprint spans ±width/2 × ±height/2.
 *
 * Anchor edge = the longer OBB edge, exactly as buildTerraceAwning picks it in
 * 3D (widthM = width·scaleX vs depthM = height·scaleY). The awning hangs there
 * and cantilevers inward over the terrace by the Ausfall — the area it shades.
 * The Ausfall is a world measure (cm ≈ world px in this editor), so it is
 * divided by the object's scale along the inward axis to land in unscaled local
 * px, and clamped to the terrace so the strip never draws past the footprint.
 *
 * @returns {null | { rail:{x1,y1,x2,y2}, coverage:null|{x,y,width,height} }}
 *   `rail` is the mounting edge (drawn even when retracted); `coverage` is the
 *   dashed strip and is null when the awning is retracted or has no reach.
 */
export function computeMarkiseSymbol({
  width,
  height,
  scaleX = 1,
  scaleY = 1,
  ausfallCm = 0,
  ausgefahren = true,
}) {
  const w = Math.max(0, width || 0);
  const h = Math.max(0, height || 0);
  if (w <= 0 || h <= 0) return null;
  const halfW = w / 2;
  const halfH = h / 2;
  const sx = Math.abs(scaleX) || 1;
  const sy = Math.abs(scaleY) || 1;

  // Longer edge wins, matching the 3D OBB comparison (scaled world dimensions).
  const useWidthEdge = w * sx >= h * sy;

  if (useWidthEdge) {
    // Anchor = top edge (local y = -halfH); the strip grows inward (+y).
    const rail = { x1: -halfW, y1: -halfH, x2: halfW, y2: -halfH };
    let coverage = null;
    if (ausgefahren && ausfallCm > 0) {
      const depth = Math.min(h, ausfallCm / sy);
      if (depth > 0) coverage = { x: -halfW, y: -halfH, width: w, height: depth };
    }
    return { rail, coverage };
  }

  // Anchor = right edge (local x = +halfW); the strip grows inward (−x).
  const rail = { x1: halfW, y1: -halfH, x2: halfW, y2: halfH };
  let coverage = null;
  if (ausgefahren && ausfallCm > 0) {
    const depth = Math.min(w, ausfallCm / sx);
    if (depth > 0) coverage = { x: halfW - depth, y: -halfH, width: depth, height: h };
  }
  return { rail, coverage };
}

/**
 * Draw a computed markise symbol onto a context already transformed into the
 * terrace's local space. The coverage strip uses the dashed "above the cutting
 * plane" convention with a translucent fill; the rail is a solid, thicker line
 * on the wall side so the retracted state (rail only) is still legible.
 */
export function drawMarkiseSymbol(ctx, symbol, { lineColor, fillColor }) {
  if (!symbol) return;
  const { rail, coverage } = symbol;

  ctx.save();

  if (coverage) {
    ctx.setLineDash?.(TERRACE_ROOF_DASH);
    ctx.lineWidth = TERRACE_ROOF_LINE_WIDTH;
    ctx.strokeStyle = lineColor;
    ctx.fillStyle = fillColor;
    ctx.fillRect(coverage.x, coverage.y, coverage.width, coverage.height);
    ctx.strokeRect(coverage.x, coverage.y, coverage.width, coverage.height);
  }

  // Mounting rail along the anchor edge: solid, no dash.
  ctx.setLineDash?.([]);
  ctx.lineWidth = MARKISE_RAIL_LINE_WIDTH;
  ctx.strokeStyle = lineColor;
  ctx.beginPath?.();
  ctx.moveTo?.(rail.x1, rail.y1);
  ctx.lineTo?.(rail.x2, rail.y2);
  ctx.stroke?.();

  ctx.restore();
}

/**
 * Compute the Wintergarten plan symbol in the terrace's local coordinate system.
 * Local origin = object center; the footprint spans ±width/2 × ±height/2.
 *
 * A Wintergarten is room-forming, so — unlike the roof and the markise, which
 * float above the cutting plane — it draws a solid, closed outline. The result
 * is a double rectangle: the `outer` edge at the footprint and an `inner` edge
 * inset by the wall thickness, which reads as a glazed wall in plan.
 *
 * @returns {null | { outer:{x,y,width,height}, inner:{x,y,width,height} }}
 *   Returns null when the variant is not 'wintergarten'.
 */
export function computeWintergartenSymbol({ width, height, ueberdachung }) {
  if (ueberdachung !== 'wintergarten') return null;

  const w = Math.max(0, width || 0);
  const h = Math.max(0, height || 0);
  const halfW = w / 2;
  const halfH = h / 2;

  const outer = { x: -halfW, y: -halfH, width: w, height: h };

  const inset = Math.min(WINTERGARTEN_WALL_INSET, halfW, halfH);
  const inner = {
    x: -halfW + inset,
    y: -halfH + inset,
    width: Math.max(0, w - 2 * inset),
    height: Math.max(0, h - 2 * inset),
  };

  return { outer, inner };
}

/**
 * Draw a computed Wintergarten symbol onto a context already transformed into
 * the terrace's local space. Solid (non-dashed) double outline with a
 * translucent fill — the room-forming convention.
 */
export function drawWintergartenSymbol(ctx, symbol, { lineColor, fillColor }) {
  if (!symbol) return;
  const { outer, inner } = symbol;

  ctx.save();

  // Solid outline — the glazed walls cut the cutting plane (room-forming).
  ctx.setLineDash?.([]);
  ctx.lineWidth = TERRACE_ROOF_LINE_WIDTH;
  ctx.strokeStyle = lineColor;
  ctx.fillStyle = fillColor;

  // Fill sets the glazed room off from the bare slab.
  ctx.fillRect(outer.x, outer.y, outer.width, outer.height);
  ctx.strokeRect(outer.x, outer.y, outer.width, outer.height);
  // Inner line = wall thickness (skipped when the footprint is too small to inset).
  if (inner.width > 0 && inner.height > 0) {
    ctx.strokeRect(inner.x, inner.y, inner.width, inner.height);
  }

  ctx.restore();
}

/**
 * Install the roof-symbol overlay on a terrace by decorating its _render.
 *
 * The shape's own renderer is captured once (`_terraceRoofBaseRender`) so
 * repeated application never wraps the wrapper. Object caching is disabled so
 * the dashed overlay is not clipped by an offscreen cache. Idempotent.
 */
export function applyTerraceRoofSymbolRendering(terrace) {
  if (!terrace) return;
  if (!terrace._terraceRoofBaseRender) {
    terrace._terraceRoofBaseRender = terrace._render;
  }
  terrace.set({ objectCaching: false });
  terrace._terraceRoofSymbolRender = true;
  terrace._render = function _renderTerraceRoof(ctx) {
    this._terraceRoofBaseRender.call(this, ctx); // the slab itself
    const variant = this.data?.ueberdachung;
    const isActive = this.canvas?.getActiveObject?.() === this;
    const lineColor = isActive ? TERRACE_ROOF_SELECTED_COLOR : TERRACE_ROOF_DEFAULT_COLOR;
    const fillColor = isActive
      ? 'rgba(16, 185, 129, 0.18)'
      : 'rgba(107, 114, 128, 0.20)';

    if (variant === 'markise') {
      const symbol = computeMarkiseSymbol({
        width: this.width,
        height: this.height,
        scaleX: this.scaleX,
        scaleY: this.scaleY,
        ausfallCm: resolveMarkiseAusfallCm(this.data),
        ausgefahren: this.data?.markiseAusgefahren !== false,
      });
      drawMarkiseSymbol(ctx, symbol, { lineColor, fillColor });
      return;
    }

    if (variant === 'wintergarten') {
      const symbol = computeWintergartenSymbol({
        width: this.width,
        height: this.height,
        ueberdachung: variant,
      });
      drawWintergartenSymbol(ctx, symbol, { lineColor, fillColor });
      return;
    }

    // terrassendach (or 'keine' → null → slab only, live read).
    const symbol = computeTerraceRoofSymbol({
      width: this.width,
      height: this.height,
      ueberdachung: variant,
    });
    drawTerraceRoofSymbol(ctx, symbol, { lineColor, fillColor });
  };
}

/**
 * Re-install the roof overlay on a terrace that currently carries a roofing
 * (called during canvas load and on the ueberdachung property change). No-op
 * for terraces without a roofing symbol.
 */
export function restoreTerraceRoofSymbolRendering(terrace) {
  if (usesTerraceRoofSymbol(terrace)) {
    applyTerraceRoofSymbolRendering(terrace);
  }
}
